'use client';
import { useEffect, useLayoutEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Thumbs } from 'swiper/modules';
import { useProduct } from '@/components/product/product-context';
import { useCampaign } from '@/app/CampaignContext';
import { findBiggestDiscount } from '@/utils/discounts';
import { findVideoAtSlide } from '@/utils/resolveImageCollection';
import GalleryMedia from './GalleryMedia';
import {
  FeaturedImageFrame,
  GalleryArrow,
  GalleryThumb,
  GalleryThumbnailsFrame,
  THUMB_SLIDE_CLASS,
  ThumbStripButton,
  VideoControls
} from './gallery-parts';

/**
 * The main PDP gallery that renders the selected variant's image collection.
 * Relies on the selected variant, so it needs to be wrapped in a Suspense tag.
 * If you update this, spare a glance at GallerySkeleton which shows the default
 * image collection while the selected variant is being resolved.
 */
export default function GalleryView({ collection }) {
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [mainSwiper, setMainSwiper] = useState(null);
  const [featureIndex, setFeatureIndex] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [controlsReadyIndex, setControlsReadyIndex] = useState(null);
  const [prevCollection, setPrevCollection] = useState(null);
  const [prevFeatureIndex, setPrevFeatureIndex] = useState(0);
  const { productData, settings, isBundlePage } = useProduct();
  const campaignContext = useCampaign();
  const showSecondaryBadge =
    settings?.secondary_badge?.badge_active ||
    settings?.badges?.pdp_badge?.text;

  const currentVideo = findVideoAtSlide(collection, featureIndex);

  // On collection change, reset to the first slide and autoplay it if it's a video.
  if (prevCollection !== collection) {
    setPrevCollection(collection);
    setFeatureIndex(0);
    setPrevFeatureIndex(0);
    setIsVideoPlaying(!!findVideoAtSlide(collection, 0));
  }

  // When the active slide changes, autoplay its video if it has one.
  if (prevFeatureIndex !== featureIndex) {
    setPrevFeatureIndex(featureIndex);
    setIsVideoPlaying(!!currentVideo);
  }

  // Secondary badge has a different structure on bundle pages
  const secondaryBadgeSettings = isBundlePage
    ? {
        bgColor: settings.badges?.pdp_badge?.background_color,
        text: settings.badges?.pdp_badge?.text,
        textColor: settings.badges?.pdp_badge?.text_color
      }
    : {
        bgColor: settings?.secondary_badge?.background_color_picker,
        text: settings?.secondary_badge?.badge_copy,
        textColor: settings?.secondary_badge?.text_color_picker
      };

  // Currently discountMsg applies only to non bundles
  let discountMsg;
  if (isBundlePage) {
    discountMsg = settings?.badges?.discount_badge?.text;
  } else {
    if (productData?.shopifyProduct) {
      discountMsg = findBiggestDiscount(
        productData.shopifyProduct.variants,
        campaignContext
      );
    }
  }

  // Reset the main and thumbnail swipers to the first slide when the collection changes.
  useEffect(() => {
    mainSwiper?.slideTo(0);
    thumbsSwiper?.slideTo(0);
  }, [collection, mainSwiper, thumbsSwiper]);

  // Clean up on unmount for proper history change handling.
  useLayoutEffect(() => {
    return () => {
      setPrevCollection(null);
    };
  }, []);

  // Reveal the play/pause overlay ~600ms after landing on a video slide.
  const currentSlideHasVideo = !!currentVideo;
  useEffect(() => {
    if (!currentSlideHasVideo) return;
    const timeout = setTimeout(() => setControlsReadyIndex(featureIndex), 600);
    return () => clearTimeout(timeout);
  }, [featureIndex, currentSlideHasVideo]);
  const onVideoSlide =
    currentSlideHasVideo && controlsReadyIndex === featureIndex;

  function handleVideoState(playing) {
    setIsVideoPlaying(playing);
  }

  function handleArrowClick(direction) {
    if (
      (direction === 'prev' && featureIndex !== 0) ||
      (direction === 'next' && featureIndex !== collection?.image?.length - 1)
    ) {
      if (direction === 'next') {
        mainSwiper.slideNext();
        setFeatureIndex(featureIndex + 1);
        thumbsSwiper?.slideNext();
      } else {
        mainSwiper.slidePrev();
        setFeatureIndex(featureIndex - 1);
        thumbsSwiper?.slidePrev();
      }
    }
  }

  return (
    <>
      <FeaturedImageFrame>
        <div className="absolute top-0 right-0 z-40 flex">
          {showSecondaryBadge && (
            <div
              className={`bg-[${secondaryBadgeSettings.bgColor}] text-[${secondaryBadgeSettings.textColor}] badge-text-r px-2 py-1`}
            >
              {secondaryBadgeSettings.text}
            </div>
          )}
          {discountMsg && (
            <div
              className={`bg-[${campaignContext.discount_tag.background_color_picker}] text-[${campaignContext.discount_tag.text_color_picker}] badge-text-r px-3 py-1`}
            >
              {discountMsg}
            </div>
          )}
        </div>
        {featureIndex !== 0 && (
          <GalleryArrow
            direction="prev"
            onClick={() => mainSwiper?.slidePrev()}
          />
        )}
        {featureIndex !== collection?.image.length - 1 && (
          <GalleryArrow
            direction="next"
            onClick={() => mainSwiper?.slideNext()}
          />
        )}
        <Swiper
          modules={[Thumbs]}
          onSwiper={(swiper) => setMainSwiper(swiper)}
          thumbs={{ swiper: thumbsSwiper?.destroyed ? null : thumbsSwiper }}
          autoHeight={true}
          onSlideChange={(swiper) => {
            setFeatureIndex(swiper.realIndex);
          }}
          className="md:rounded-11 w-full"
        >
          {(collection?.image || []).map((image, index) => {
            if (!image) return null;

            return (
              <SwiperSlide
                key={index}
                className="block w-full md:overflow-hidden"
              >
                <GalleryMedia
                  image={image}
                  video={findVideoAtSlide(collection, index)}
                  isActive={featureIndex === index}
                  priority={index === 0}
                  playing={featureIndex === index && isVideoPlaying}
                  onPlay={() => handleVideoState(true)}
                  onPause={() => handleVideoState(false)}
                />
                {index === featureIndex && onVideoSlide && (
                  <VideoControls
                    playing={isVideoPlaying}
                    onPlay={() => handleVideoState(true)}
                    onPause={() => handleVideoState(false)}
                  />
                )}
              </SwiperSlide>
            );
          })}
        </Swiper>
      </FeaturedImageFrame>

      <GalleryThumbnailsFrame>
        <ThumbStripButton
          direction="prev"
          onClick={() => handleArrowClick('prev')}
        />
        <Swiper
          modules={[Thumbs]}
          onSwiper={setThumbsSwiper}
          watchSlidesProgress
          spaceBetween={20}
          centerInsufficientSlides={true}
          className="w-full"
          slidesPerView={3}
          breakpoints={{ 768: { slidesPerView: 5 } }}
        >
          {(collection?.image || []).map((image, index) => {
            if (!image) return null;

            return (
              <SwiperSlide
                key={index}
                onClick={() => mainSwiper?.slideTo(index)}
                data-cy="gallery-thumbnail-slide"
                className={`${THUMB_SLIDE_CLASS} mr-5 cursor-pointer`}
              >
                <GalleryThumb
                  image={image}
                  isActive={index === featureIndex}
                  hasVideo={!!findVideoAtSlide(collection, index)}
                />
              </SwiperSlide>
            );
          })}
        </Swiper>
        <ThumbStripButton
          direction="next"
          onClick={() => handleArrowClick('next')}
        />
      </GalleryThumbnailsFrame>
    </>
  );
}
